// frontend/src/features/inventory/pages/LotsPage.tsx
// ADR-075 Rule DU-05: مدیریت پالت‌ها و اصلاح ضریب تبدیل (adjust-conversion)

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import {
  useV1InventoryLotsList,
  useV1InventoryLotsAdjustConversionCreate,
  useV1InventoryWarehousesList,
  useV1InventoryMaterialsList,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";
import { LotStatusHistoryModal, getStatusBadge } from "../components/LotStatusHistoryModal";

export function LotsPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();

  const [materialFilter, setMaterialFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [expandedLotId, setExpandedLotId] = useState<string | null>(null);
  const [selectedLotForStatus, setSelectedLotForStatus] = useState<any | null>(null);

  // فرم اصلاح ضریب
  const [adjustNumerator, setAdjustNumerator] = useState("");
  const [adjustDenominator, setAdjustDenominator] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: lotsData, isLoading: lotsLoading, refetch: refetchLots } =
    useV1InventoryLotsList(
      {
        ...(materialFilter ? { material_revision__family: materialFilter } : {}),
        ...(warehouseFilter ? { warehouse: warehouseFilter } : {}),
      } as any,
      { query: {} }
    );

  const { data: warehousesData } = useV1InventoryWarehousesList(undefined, { query: {} });
  const { data: materialsData } = useV1InventoryMaterialsList(undefined, { query: {} });

  const { mutate: adjustConversion, isPending: isAdjusting } =
    useV1InventoryLotsAdjustConversionCreate({
      mutation: {
        onSuccess: () => {
          setExpandedLotId(null);
          setAdjustNumerator("");
          setAdjustDenominator("");
          setAdjustReason("");
          setError(null);
          refetchLots();
        },
        onError: (err: any) => {
          setError(
            err?.response?.data?.detail ||
            err?.response?.data?.reason?.[0] ||
            "خطا در ثبت اصلاح ضریب"
          );
        },
      },
    });

  const lots = lotsData?.results ?? [];
  const warehouses = warehousesData?.results ?? [];
  const materials = materialsData?.results ?? [];

  const handleOpenAdjust = (lot: any) => {
    setExpandedLotId(lot.id);
    setAdjustNumerator(String(lot.effective_numerator ?? ""));
    setAdjustDenominator(String(lot.effective_denominator ?? ""));
    setAdjustReason("");
    setError(null);
  };

  const handleSubmitAdjust = (lotId: string) => {
    setError(null);
    if (!adjustNumerator || !adjustDenominator) {
      setError("هر دو ضریب باید مشخص شوند");
      return;
    }
    if (!adjustReason.trim()) {
      setError("دلیل اصلاح ضریب اجباری است");
      return;
    }
    adjustConversion({
      id: lotId,
      data: {
        adjusted_numerator: adjustNumerator,
        adjusted_denominator: adjustDenominator,
        reason: adjustReason.trim(),
      } as any,
    });
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="پالت‌ها (Lots)"
        subtitle="مدیریت پالت‌ها و اصلاح ضریب تبدیل"
        icon="🔖"
        helpLink="/help/inventory/lots"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        {/* فیلترها */}
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <div style={{ flex: 1, minWidth: "180px" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              متریال
            </label>
            <select
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              style={{
                width: "100%", padding: "0.6rem 0.9rem",
                background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
              }}
            >
              <option value="">همه متریال‌ها</option>
              {materials.map((m: any) => (
                <option key={m.id} value={m.id}>{m.code}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: "180px" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              انبار
            </label>
            <select
              value={warehouseFilter}
              onChange={(e) => setWarehouseFilter(e.target.value)}
              style={{
                width: "100%", padding: "0.6rem 0.9rem",
                background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
              }}
            >
              <option value="">همه انبارها</option>
              {warehouses.map((w: any) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* لیست پالت‌ها */}
        {lotsLoading ? (
          <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
        ) : lots.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", textAlign: "center", padding: "2rem" }}>
            هیچ پالتی یافت نشد.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {lots.map((lot: any) => {
              const materialCode = materials.find((m: any) => m.id === String(lot.material_revision))?.code;
              const warehouseName = warehouses.find((w: any) => w.id === String(lot.warehouse))?.name;
              const isExpanded = expandedLotId === lot.id;

              return (
                <div
                  key={lot.id}
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "10px",
                    padding: "1rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <div style={{ fontWeight: "bold", fontSize: "1rem" }} dir="ltr">
                          {lot.lot_number}
                        </div>
                        {getStatusBadge(lot.effective_status)}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
                        {materialCode || "—"} · {warehouseName || "—"} · موجودی اولیه: {lot.received_qty}
                      </div>
                      <div style={{ fontSize: "0.85rem", marginTop: "0.25rem" }} dir="ltr">
                        ضریب فعلی: {lot.effective_numerator} / {lot.effective_denominator}
                        {lot.conversion_adjustments?.length > 0 && (
                          <span style={{ color: "var(--color-text-muted)", marginRight: "0.5rem" }}>
                            (اصلاح‌شده — {lot.conversion_adjustments.length} بار)
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                        onClick={() => setSelectedLotForStatus(lot)}
                      >
                        وضعیت و ردپایی
                      </button>
                      {canManage && (
                        <>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                            onClick={() => navigate(`/inventory/lots/${lot.id}/print`)}
                          >
                            چاپ برچسب
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                            onClick={() => (isExpanded ? setExpandedLotId(null) : handleOpenAdjust(lot))}
                          >
                            {isExpanded ? "انصراف" : "اصلاح ضریب"}
                          </button>
                        </>
                      )}
                    </div>

                  </div>

                  {/* تاریخچه اصلاحات قبلی (آخرین ۲ مورد) */}
                  {lot.conversion_adjustments?.length > 0 && !isExpanded && (
                    <div style={{ marginTop: "0.75rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                      {lot.conversion_adjustments
                        .slice()
                        .reverse()
                        .slice(0, 2)
                        .map((adj: any) => (
                          <div key={adj.id} style={{ marginTop: "0.25rem" }}>
                            {adj.adjusted_by_email || "—"}: {adj.reason}
                            {" "}
                            <span dir="ltr">({adj.adjusted_numerator}/{adj.adjusted_denominator})</span>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* فرم اصلاح ضریب */}
                  {isExpanded && (
                    <div
                      style={{
                        marginTop: "1rem", padding: "1rem",
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid var(--glass-border)", borderRadius: "8px",
                      }}
                    >
                      {error && (
                        <div
                          className="mb-3 p-3"
                          style={{
                            background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red",
                            borderRadius: "8px", color: "red", fontSize: "0.9rem",
                          }}
                        >
                          {error}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                        <div style={{ flex: 1, minWidth: "120px" }}>
                          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                            ضریب جدید واحد تولید *
                          </label>
                          <input
                            type="number" value={adjustNumerator}
                            onChange={(e) => setAdjustNumerator(e.target.value)}
                            dir="ltr"
                            style={{
                              width: "100%", padding: "0.6rem 0.9rem",
                              background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                              borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: "120px" }}>
                          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                            ضریب جدید واحد انبار *
                          </label>
                          <input
                            type="number" value={adjustDenominator}
                            onChange={(e) => setAdjustDenominator(e.target.value)}
                            dir="ltr"
                            style={{
                              width: "100%", padding: "0.6rem 0.9rem",
                              background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                              borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div style={{ flex: 2, minWidth: "220px" }}>
                          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                            دلیل اصلاح *
                          </label>
                          <input
                            type="text" value={adjustReason}
                            onChange={(e) => setAdjustReason(e.target.value)}
                            placeholder="مثلاً: اصلاح بر اساس نتیجه بازرسی QC"
                            style={{
                              width: "100%", padding: "0.6rem 0.9rem",
                              background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)",
                              borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "0.75rem" }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleSubmitAdjust(lot.id)}
                          disabled={isAdjusting}
                        >
                          {isAdjusting ? "در حال ثبت..." : "ثبت اصلاح"}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => { setExpandedLotId(null); setError(null); }}
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedLotForStatus && (
        <LotStatusHistoryModal
          isOpen={!!selectedLotForStatus}
          lot={selectedLotForStatus}
          onClose={() => setSelectedLotForStatus(null)}
          onStatusChanged={() => refetchLots()}
        />
      )}
    </div>
  );
}

